import { getHost } from '@b-stock/fe-core/bstock-next/helpers'

export type CardAddOperation = {
  token: string
  operationId: string
  expiresAt: string
}

export async function cardAddRequest<T>(
  accountId: string,
  accessToken: string,
  action: 'card-add-operations' | 'card-add-completions',
  body?: object,
  signal?: AbortSignal
): Promise<T> {
  const response = await fetch(
    `${getHost('PAYMETHODS')}/v1/balance/${encodeURIComponent(accountId)}/${action}`,
    {
      method: 'POST',
      cache: 'no-store',
      signal,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body ?? {}),
    }
  )
  // Gateways can return HTML/empty 5xx bodies, and completion may return no
  // body. HTTP status determines success/retry; JSON is only error metadata.
  const data: unknown = await response.json().catch(() => null)
  if (!response.ok) {
    const pending =
      response.status === 409 &&
      typeof data === 'object' &&
      data !== null &&
      'meta' in data &&
      typeof data.meta === 'object' &&
      data.meta !== null &&
      'errorCode' in data.meta &&
      data.meta.errorCode === 'BALANCE_CARD_PENDING'
    throw Object.assign(new Error('Card add could not be completed'), {
      pending,
      retryable: response.status >= 500,
    })
  }
  return data as T
}

export async function completeCardAdd(
  accountId: string,
  accessToken: string | (() => string),
  operationId: string,
  paymentMethodId: string,
  signal?: AbortSignal,
  isCurrent: () => boolean = () => true
): Promise<void> {
  let transientFailures = 0
  for (let attempt = 0; attempt < 20; attempt++) {
    signal?.throwIfAborted()
    if (!isCurrent()) throw new Error('Card add session changed')
    const currentToken =
      typeof accessToken === 'function' ? accessToken() : accessToken
    if (!currentToken) throw new Error('Authenticated person is required')
    try {
      await cardAddRequest(
        accountId,
        currentToken,
        'card-add-completions',
        { operationId, paymentMethodId },
        signal
      )
      return
    } catch (error) {
      if (!(error instanceof Error) || attempt === 19) throw error
      const pending = 'pending' in error && error.pending === true
      const retryable =
        error instanceof TypeError ||
        ('retryable' in error && error.retryable === true)
      if (!pending && (!retryable || ++transientFailures >= 3)) throw error
      await new Promise<void>((resolve, reject) => {
        const aborted = () => {
          clearTimeout(timer)
          reject(new Error('Card add canceled'))
        }
        const timer = setTimeout(() => {
          signal?.removeEventListener('abort', aborted)
          resolve()
        }, 1000)
        signal?.addEventListener('abort', aborted, { once: true })
      })
    }
  }
}
